import React, { useState, useEffect, lazy, Suspense } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Tabs, 
  Tab, 
  CircularProgress,
  Alert
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthModule';
import ErrorBoundary from '../../components/shared/ErrorBoundary';
import LoadingSuspense from '../../components/shared/LoadingSuspense';
import { useTranslation } from 'react-i18next';

// Lazy load tab components to improve performance
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const UsersTab = lazy(() => import('./components/UsersTab'));
const CarsTab = lazy(() => import('./components/CarsTab'));
const TasksTab = lazy(() => import('./components/TasksTab'));
const ReplacementsTab = lazy(() => import('./components/ReplacementsTab'));
const EventsTab = lazy(() => import('./components/EventsTab'));
const RacetracksTab = lazy(() => import('./components/RacetracksTab'));
const SponsorsTab = lazy(() => import('./sponsors/SponsorsManagementPage'));
const SiteSettingsTab = lazy(() => import('./components/SiteSettingsTab'));

const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(['common', 'admin']);

  // Parse the tab from the URL query parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    
    if (tab) {
      switch (tab) {
        case 'users':
          setActiveTab(1);
          break;
        case 'cars':
          setActiveTab(2);
          break;
        case 'tasks':
          setActiveTab(3);
          break;
        case 'replacements':
          setActiveTab(4);
          break;
        case 'events':
          setActiveTab(5);
          break;
        case 'racetracks':
          setActiveTab(6);
          break;
        case 'sponsors':
          setActiveTab(7);
          break;
        case 'settings':
          setActiveTab(8);
          break;
        default:
          setActiveTab(0);
      }
    } else {
      setActiveTab(0);
    }
  }, [location]);

  // Redirect non-admin users
  useEffect(() => {
    if (user && !user.isAdmin) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    
    // Update the URL query parameter
    const tabNames = ['', 'users', 'cars', 'tasks', 'replacements', 'events', 'racetracks', 'sponsors', 'settings'];
    if (newValue === 0) {
      navigate('/admin');
    } else {
      navigate(`/admin?tab=${tabNames[newValue]}`);
    }
  };

  // Reset error boundary when changing tabs
  const handleErrorReset = () => {
    // Force a re-render of the current tab
    setActiveTab(prev => {
      // Toggle to a dummy value and back to force re-render
      const temp = prev === 999 ? activeTab : 999;
      setTimeout(() => setActiveTab(prev), 0);
      return temp;
    });
  };

  if (!user) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!user.isAdmin) {
    return (
      <Container maxWidth="lg">
        <Box mt={4}>
          <Alert severity="error">
            {t('admin:errors.noPermission')}
          </Alert>
        </Box>
      </Container>
    );
  }

  // Render the active tab component
  const renderTabContent = () => {
    // Use a key based on the active tab to ensure a fresh instance when switching tabs
    const key = `tab-${activeTab}`;
    
    return (
      <ErrorBoundary key={key} onReset={handleErrorReset}>
        <LoadingSuspense message={t('admin:errors.loadingMessage', { tab: getTabName(activeTab) })}>
          {activeTab === 0 && <AdminDashboard />}
          {activeTab === 1 && <UsersTab />}
          {activeTab === 2 && <CarsTab />}
          {activeTab === 3 && <TasksTab />}
          {activeTab === 4 && <ReplacementsTab />}
          {activeTab === 5 && <EventsTab />}
          {activeTab === 6 && <RacetracksTab />}
          {activeTab === 7 && <SponsorsTab />}
          {activeTab === 8 && <SiteSettingsTab />}
        </LoadingSuspense>
      </ErrorBoundary>
    );
  };

  // Helper function to get tab name for loading message
  const getTabName = (tabIndex) => {
    return t(`admin:tabs.${['dashboard', 'users', 'cars', 'tasks', 'replacements', 'events', 'racetracks', 'sponsors', 'settings'][tabIndex]}`);
  };

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        
        <Paper sx={{ mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={t('admin:tabs.dashboard')} />
            <Tab label={t('admin:tabs.users')} />
            <Tab label={t('admin:tabs.cars')} />
            <Tab label={t('admin:tabs.tasks')} />
            <Tab label={t('admin:tabs.replacements')} />
            <Tab label={t('admin:tabs.events')} />
            <Tab label={t('admin:tabs.racetracks')} />
            <Tab label={t('admin:tabs.sponsors')} />
            <Tab label={t('admin:tabs.settings')} />
          </Tabs>
          
          <Box p={3}>
            {renderTabContent()}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AdminPage; 